/**
 * BoothPairingPage — /booth
 *
 * The public pairing entry point. A booth attendant on event night
 * opens eventdrop.co/booth on the kiosk tablet, types the 6-digit
 * code the host read off their Setup tab, and the tablet locks itself
 * into the booth for that one event. Spotify-Connect tone, not
 * enterprise-device-provisioning.
 *
 * Decisions locked (Craig 2026-02 fork):
 *   • Single large 6-digit input · large keypad on touch.
 *   • Resolves via POST /api/booth-pair/redeem (public, no auth).
 *   • On success → SPA-navigates to /booth-os/{event_id} and the
 *     kiosk shell takes over (it will persist the event_id locally
 *     so an accidental refresh re-opens the same night).
 *   • Pairing grants kiosk access ONLY — never dashboard / organiser
 *     / account authority. Server enforces this.
 *
 * Hard bans (must comply): no cyan/neon, no SaaS chrome, no glow,
 * no "Welcome admin!" enterprise tone, no analytics widgets.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAPER_BG  = '#1c1108';
const PAPER_HI  = '#2a190a';
const INK       = '#FFE7A8';
const INK_MUTED = 'rgba(255,235,210,0.62)';
const INK_FAINT = 'rgba(255,235,210,0.38)';
const GOLD      = '#D4AF5E';
const GOLD_DEEP = '#B8923F';
const HAIR      = 'rgba(212,170,82,0.32)';
const SERIF     = 'Georgia, "Times New Roman", serif';

const CODE_LENGTH = 6;

const BoothPairingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [status, setStatus] = useState('idle'); // 'idle' | 'redeeming' | 'opening' | 'error'
  const [error, setError] = useState(null);
  const [resolvedEvent, setResolvedEvent] = useState(null);
  const [arrivedViaScan, setArrivedViaScan] = useState(false);
  const inputRef = useRef(null);

  // The visible string for the hidden keyboard input.
  const codeString = digits.join('');

  const setCode = useCallback((raw) => {
    const cleaned = (raw || '').replace(/\D/g, '').slice(0, CODE_LENGTH);
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < cleaned.length; i++) next[i] = cleaned[i];
    setDigits(next);
    setError(null);
    setStatus('idle');
  }, []);

  const handleKeyDigit = (n) => {
    if (status === 'redeeming' || status === 'opening') return;
    const next = [...digits];
    const firstEmpty = next.findIndex((d) => d === '');
    if (firstEmpty === -1) return;
    next[firstEmpty] = String(n);
    setDigits(next);
    setError(null);
  };

  const handleBackspace = () => {
    if (status === 'redeeming' || status === 'opening') return;
    const next = [...digits];
    for (let i = CODE_LENGTH - 1; i >= 0; i--) {
      if (next[i] !== '') { next[i] = ''; break; }
    }
    setDigits(next);
    setError(null);
  };

  const handleClear = () => setCode('');

  // Auto-submit the moment the 6th digit lands. Ceremony, not friction.
  useEffect(() => {
    if (codeString.length === CODE_LENGTH && status === 'idle') {
      redeem(codeString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeString]);

  const redeem = async (code, viaScan = false) => {
    setStatus('redeeming');
    setError(null);
    try {
      const r = await axios.post(`${API}/booth-pair/redeem`, { code });
      const ev = r.data;
      setResolvedEvent(ev);
      setStatus('opening');
      // Remember the pairing so an accidental refresh on the kiosk
      // walks the operator straight back into the same night.
      try {
        localStorage.setItem('booth_os_paired_event_id', ev.event_id);
        localStorage.setItem('booth_os_paired_event_name', ev.event_name || '');
      } catch {}
      // Short ceremonial dwell so the host watches the room "open"
      // before the kiosk locks in.
      setTimeout(() => navigate(`/booth-os/${ev.event_id}`), 1100);
    } catch (e) {
      // QR-arrival error language is softer — the operator didn't
      // mistype, the code simply rotated. Reset to keypad-ready state.
      const defaultMsg = viaScan
        ? 'This code has settled. Enter a current pairing code ✦'
        : 'That code didn’t open the room. Try again.';
      const serverMsg = e?.response?.data?.detail;
      const msg = (viaScan && (e?.response?.status === 404)) ? defaultMsg : (serverMsg || defaultMsg);
      setError(msg);
      setStatus('error');
      // Clear the slots so the operator has a fresh canvas.
      setDigits(['', '', '', '', '', '']);
      // Strip the bad code from the URL so a refresh doesn't loop.
      if (viaScan) {
        const next = new URLSearchParams(searchParams);
        next.delete('code');
        setSearchParams(next, { replace: true });
      }
    }
  };

  // ── QR-arrival prefill ────────────────────────────────────────────
  // If the operator scans the QR on the host's Setup card we land
  // here as `/booth?code=916258`. Validate, prefill, and let the
  // existing auto-redeem effect take over. If the server rejects
  // the code, `redeem()` softly falls back to the keypad state with
  // the editorial "This code has settled ✦" message.
  useEffect(() => {
    const raw = searchParams.get('code');
    if (!raw) return;
    const cleaned = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (cleaned.length !== CODE_LENGTH) return;
    setArrivedViaScan(true);
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < CODE_LENGTH; i++) next[i] = cleaned[i];
    setDigits(next);
    // Mark as actively redeeming immediately so the codeString effect
    // doesn't double-fire on the same tick.
    setStatus('redeeming');
    // Dwell a beat so the operator sees the code land before the
    // kiosk opens — the QR shortcut should still feel intentional.
    setTimeout(() => redeem(cleaned, true), 260);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Physical keyboard support for laptops being used as kiosks.
  useEffect(() => {
    const onKey = (e) => {
      if (status === 'redeeming' || status === 'opening') return;
      if (/^\d$/.test(e.key)) {
        handleKeyDigit(parseInt(e.key, 10));
      } else if (e.key === 'Backspace') {
        handleBackspace();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits, status]);

  return (
    <div
      data-testid="booth-pairing-shell"
      style={{
        minHeight: '100vh',
        background: `radial-gradient(1300px 800px at 50% -120px, ${PAPER_HI} 0%, ${PAPER_BG} 60%, #0A0604 100%)`,
        color: INK,
        fontFamily: SERIF,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(32px, 5vw, 64px) 24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <header style={{ textAlign: 'center', maxWidth: 620, marginBottom: 'clamp(28px, 4vw, 48px)' }}>
        <div style={{
          fontFamily: SERIF, fontWeight: 800,
          fontSize: 11.5, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD, marginBottom: 18,
        }}>✦ EVENTDROP BOOTH ✦</div>
        <h1 style={{
          margin: 0, fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(32px, 5vw, 54px)', lineHeight: 1.08,
          letterSpacing: '-0.012em', color: INK,
        }}>
          Bring the room to life ✦
        </h1>
        <p style={{
          margin: '16px 0 0', fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.4vw, 18px)', color: INK_MUTED,
          lineHeight: 1.55,
        }}>
          Enter the pairing code from the host’s register ✦
        </p>
      </header>

      {/* Six-digit slot row */}
      <div
        onClick={() => inputRef.current?.focus()}
        data-testid="booth-pairing-slots"
        style={{
          display: 'flex', gap: 'clamp(8px, 1.6vw, 14px)',
          marginBottom: 'clamp(20px, 3vw, 32px)',
          cursor: 'text',
        }}
      >
        {digits.map((d, i) => {
          const isActive = codeString.length === i;
          const isFilled = !!d;
          return (
            <div
              key={i}
              data-testid={`pairing-slot-${i}`}
              style={{
                width: 'clamp(46px, 7vw, 72px)',
                height: 'clamp(64px, 9vw, 96px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(20,12,6,0.72)',
                border: `1px solid ${isFilled || isActive ? GOLD : HAIR}`,
                borderRadius: 6,
                fontFamily: SERIF, fontWeight: 500,
                fontSize: 'clamp(28px, 5vw, 44px)',
                color: INK, letterSpacing: '-0.02em',
                boxShadow: isActive
                  ? `inset 0 0 0 1px ${GOLD}, 0 0 22px rgba(212,175,94,0.18)`
                  : '0 6px 18px rgba(0,0,0,0.32)',
                transition: 'border-color 220ms ease, box-shadow 220ms ease',
                position: 'relative',
              }}
            >
              {d || (isActive && status === 'idle' ? (
                <span style={{
                  width: 2, height: '52%', background: GOLD,
                  animation: 'pairBlink 1.1s ease-in-out infinite',
                }} />
              ) : '')}
              {/* hairline separator between groups of three */}
              {i === 2 && (
                <span style={{
                  position: 'absolute', right: -8,
                  fontSize: 24, color: INK_FAINT, fontStyle: 'italic',
                  pointerEvents: 'none',
                }}>·</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Hidden native input — gives mobile the right keyboard. */}
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        value={codeString}
        onChange={(e) => setCode(e.target.value)}
        data-testid="pairing-hidden-input"
        aria-label="Booth pairing code"
        style={{
          position: 'absolute', opacity: 0, pointerEvents: 'none',
          width: 1, height: 1,
        }}
      />

      {/* Status copy */}
      <div
        data-testid="pairing-status"
        style={{
          minHeight: 28, textAlign: 'center',
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 14, color: status === 'error' ? '#FFAA90'
            : status === 'opening' ? GOLD
            : INK_MUTED,
          letterSpacing: '0.04em',
          margin: '0 0 clamp(20px, 2.4vw, 28px)',
          animation: status !== 'idle' ? 'pairFade .4s ease-out' : 'none',
        }}
      >
        {status === 'redeeming' && 'Opening the room…'}
        {status === 'opening' && resolvedEvent && (
          <>
            ✦ {resolvedEvent.event_name || 'The night'} is awake.
            Stepping inside…
          </>
        )}
        {status === 'error' && error}
        {status === 'idle' && (codeString.length > 0
          ? `${codeString.length} of ${CODE_LENGTH} ✦`
          : 'Six digits ✦')}
      </div>

      {/* Large tactile keypad (touch-first) */}
      <Keypad
        onDigit={handleKeyDigit}
        onBackspace={handleBackspace}
        onClear={handleClear}
        disabled={status === 'redeeming' || status === 'opening'}
      />

      <footer style={{
        marginTop: 'clamp(28px, 4vw, 44px)', textAlign: 'center',
      }}>
        <p style={{
          margin: 0, fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12.5, color: INK_FAINT, letterSpacing: '0.04em',
        }}>
          The host can read the code from their EventPage · Setup · Booth Pairing ✦
        </p>
      </footer>

      <style>{`
        @keyframes pairBlink { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
        @keyframes pairFade  { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        *::-webkit-scrollbar { display: none; }
        * { -webkit-tap-highlight-color: transparent; }
        button { font-family: inherit; }
      `}</style>
    </div>
  );
};

const Keypad = ({ onDigit, onBackspace, onClear, disabled }) => {
  const rows = [[1,2,3], [4,5,6], [7,8,9]];
  return (
    <div
      data-testid="booth-pairing-keypad"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(72px, 96px))',
        gap: 'clamp(8px, 1.4vw, 14px)',
        width: '100%', maxWidth: 360,
      }}
    >
      {rows.flat().map((n) => (
        <KeypadButton key={n} onClick={() => onDigit(n)} disabled={disabled} testId={`pad-${n}`}>
          {n}
        </KeypadButton>
      ))}
      <KeypadButton onClick={onClear} disabled={disabled} testId="pad-clear" variant="ghost">
        Clear
      </KeypadButton>
      <KeypadButton onClick={() => onDigit(0)} disabled={disabled} testId="pad-0">
        0
      </KeypadButton>
      <KeypadButton onClick={onBackspace} disabled={disabled} testId="pad-back" variant="ghost">
        ←
      </KeypadButton>
    </div>
  );
};

const KeypadButton = ({ children, onClick, disabled, testId, variant = 'solid' }) => {
  const [pressed, setPressed] = useState(false);
  const ghost = variant === 'ghost';
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={() => !disabled && setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      disabled={disabled}
      data-testid={testId}
      style={{
        height: 'clamp(56px, 8vw, 76px)',
        background: ghost
          ? 'transparent'
          : pressed ? 'rgba(212,170,82,0.16)' : 'rgba(20,12,6,0.72)',
        border: `1px solid ${pressed ? GOLD : HAIR}`,
        borderRadius: 8,
        color: INK,
        fontFamily: SERIF, fontWeight: ghost ? 500 : 400,
        fontStyle: ghost ? 'italic' : 'normal',
        fontSize: ghost ? 'clamp(14px, 1.5vw, 17px)' : 'clamp(22px, 3vw, 28px)',
        letterSpacing: ghost ? '0.04em' : '-0.005em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 160ms ease, border-color 200ms ease, background 200ms ease',
      }}
    >
      {children}
    </button>
  );
};

export default BoothPairingPage;
